import { useCallback, useEffect, useState } from 'react';
import { GuaranteeDto } from 'api/pricing';
import { usePanel } from 'hooks/usePanel';
import { BorrowerContractFunnelV2Dto } from 'api/pricing';
import { useMainBorrowerPricingActions } from '../../../contexts/main-borrower-pricing/MainBorrowerPricingContext';

interface UseBorrowerContractGuaranteesProps {
  contractFunnel: BorrowerContractFunnelV2Dto;
}

export interface GuaranteesValuesWarning {
  value: string,
  warningMessage?: string,
}

interface GuaranteeValues {
  psyBuyback: boolean
  contribExemption: boolean
}

export const useBorrowerContractGuarantees = ({ contractFunnel }: UseBorrowerContractGuaranteesProps) => {
  const [activeGuarantees, setActiveGuarantees] = useState<GuaranteeDto[]>(contractFunnel.guarantees);
  const [guaranteeValues, setGuaranteeValues] = useState<Record<string, GuaranteeValues>>({});

  const { setPanelActions, closePanel } = usePanel();
  const { updateContractFunnelGuarantees, onAdjustments } = useMainBorrowerPricingActions();

  // Initialiser les valeurs des garanties
  useEffect(() => {
    const initialValues: Record<string, GuaranteeValues> = {};
    // contractFunnel?.guarantees.forEach(guarantee => {
    //   const guaranteeValue: GuaranteeValues = {};
    //   if (guarantee.psyBuyback?.value) {
    //     guaranteeValue.psyBuyback = {
    //       value: guarantee.psyBuyback.value.toString(),
    //       warningMessage: guarantee.psyBuyback.warningMessage ? guarantee.psyBuyback.warningMessage : undefined,
    //     }
    //   }
    //   if (guarantee.contribExemption?.value) {
    //     guaranteeValue.contribExemption = {
    //       value: guarantee.contribExemption.value.toString(),
    //       warningMessage: guarantee.contribExemption.warningMessage ? guarantee.contribExemption.warningMessage : undefined,
    //     }
    //   }
    //   if (guaranteeValue.psyBuyback || guaranteeValue.contribExemption) {
    //     initialValues[guarantee.id] = guaranteeValue;
    //   }
    // });
    setGuaranteeValues(initialValues);
  }, [contractFunnel]);

  const updatePsyBuyback = useCallback((guaranteeId: string, value: boolean) => {
    setGuaranteeValues(prev => ({
      ...prev,
      [guaranteeId]: {
        ...prev[guaranteeId],
        psyBuyback: value,
      }
    }));
  }, []);

  const updateContribExemption = useCallback((guaranteeId: string, value: boolean) => {
    setGuaranteeValues(prev => ({
      ...prev,
      [guaranteeId]: {
        ...prev[guaranteeId],
        contribExemption: value
      }
    }));
  }, []);

  const getGuaranteeValue = useCallback((guaranteeId: string): GuaranteeValues => {
    return guaranteeValues[guaranteeId] || {};
  }, [guaranteeValues]);

  const isActive = useCallback((guaranteeId: string): boolean => {
    return activeGuarantees.some(g => g.id === guaranteeId);
  }, [activeGuarantees]);

  const toggleGuarantee = useCallback((guarantee: GuaranteeDto) => {
    setActiveGuarantees(prev => {
      const isCurrentlyActive = prev.some(g => g.id === guarantee.id);
      if (isCurrentlyActive) {
        return prev.filter(g => g.id !== guarantee.id);
      } else {
        return [...prev, guarantee];
      }
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    const newGuarantees = activeGuarantees.map((guarantee) => {
      const currentGuaranteeValues = getGuaranteeValue(guarantee.id);

      return {
        ...guarantee,
        contribExemption: currentGuaranteeValues.contribExemption,
        psyBuyback: currentGuaranteeValues.psyBuyback
      };
    });

    updateContractFunnelGuarantees({
      contractFunnelId: contractFunnel.id,
      contractIdentifier: contractFunnel.contractIdentifier,
      newGuarantees,
    });

    closePanel();

    // Recalculer les prix après modification des garanties
    onAdjustments();
  }, [activeGuarantees, getGuaranteeValue, updateContractFunnelGuarantees, contractFunnel.id, contractFunnel.contractIdentifier, closePanel, onAdjustments]);

  useEffect(() => {
    setPanelActions({
      closeLabel: "COMMON.CANCEL",
      submitLabel: "Valider",
      onSubmit: handleSubmit
    });
  }, [handleSubmit, setPanelActions]);

  return {
    activeGuarantees,
    guaranteeValues,
    updatePsyBuyback,
    updateContribExemption,
    getGuaranteeValue,
    isActive,
    toggleGuarantee,
    handleSubmit
  };
};
